import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { DISPUTES } from "@/mockData/disputes";

export interface DisputesWidgetSummaryApi {
  unresolvedCount: number;
  unresolvedTotalAmount: number;
}

/**
 * Hook to fetch disputes widget summary for the dashboard.
 * @param assignedUserId — when provided (live mode only), passes `?assignedUserId=<id>` so the
 *   backend returns counts scoped to that user. Pass `undefined` for the "Everyone" view.
 */
export function useDisputesWidgetSummary(assignedUserId?: string) {
  const { apiGet } = useApi();

  // In demo mode filter disputes by assigned user (or all if no user selected)
  const filteredDisputes = assignedUserId
    ? DISPUTES.filter((d) => d.assignedTo === assignedUserId)
    : DISPUTES;
  const demoData: DisputesWidgetSummaryApi = {
    unresolvedCount: filteredDisputes.length,
    unresolvedTotalAmount: filteredDisputes.reduce((sum, x) => sum + x.amount, 0),
  };

  // Build query params — only include assignedUserId when a specific user is selected
  const params: Record<string, string | number | boolean | undefined> | undefined =
    assignedUserId ? { assignedUserId } : undefined;

  return useModeAwareQuery<DisputesWidgetSummaryApi, Error>(
    ["disputes-widget-summary", assignedUserId ?? "everyone"],
    async ({ signal }) => {
      return apiGet<DisputesWidgetSummaryApi>(
        "/api/v1/disputes/widget-summary",
        params,
        signal
      );
    },
    demoData,
    { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false }
  );
}
